import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${API_URL}/api/public/docs/warm-handover/pdf`;

export default function WarmHandoverRedirect() {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('src') || 'direct';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const n = name.trim();
    const em = email.trim();
    if (!n) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setError('Please enter a valid email address.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/docs/warm-handover/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n, email: em, source }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Something went wrong. Please try again.'); return; }
      setDone(true);
      window.open(PDF_URL, '_blank', 'noopener');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6" data-testid="warm-handover-gate">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <FileText size={40} className="mx-auto mb-4 text-purple-600" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#1A1209' }}>
            Warm Handover White Paper
          </h1>
          <p className="text-sm text-gray-600">
            A Safety Standard for AI Conversations with Young People — published October 2026.
            Tell us who&apos;s reading and we&apos;ll open the PDF.
          </p>
        </div>

        {done ? (
          <div className="text-center p-6 rounded-xl border border-green-200 bg-green-50" data-testid="warm-handover-success">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-green-600" />
            <p className="text-sm font-semibold mb-2" style={{ color: '#1A1209' }}>Thank you, {name.trim()}!</p>
            <p className="text-sm text-gray-600">
              The white paper opened in a new tab. If it didn&apos;t,{' '}
              <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline font-semibold" data-testid="warm-handover-direct-link">
                click here to read the PDF
              </a>.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 rounded-xl border border-gray-200 shadow-sm space-y-4" data-testid="warm-handover-form">
            <div>
              <label htmlFor="wh-name" className="block text-xs font-semibold mb-1" style={{ color: '#44403A' }}>Your name</label>
              <input
                id="wh-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="warm-handover-name-input"
              />
            </div>
            <div>
              <label htmlFor="wh-email" className="block text-xs font-semibold mb-1" style={{ color: '#44403A' }}>Email address</label>
              <input
                id="wh-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="warm-handover-email-input"
              />
            </div>
            {error && <p className="text-xs text-red-600" data-testid="warm-handover-error">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-lg bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="warm-handover-submit-btn"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Opening…</> : 'Read the White Paper'}
            </button>
            <p className="text-[11px] text-gray-500 text-center">
              We only use your details to understand who&apos;s reading our research. No marketing lists, no third parties — ever.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
